import { clearError, defineElement, displayError } from '~/utils/gw-utils';
import { validateEmail, validatePhone } from '~/utils/address-utils';

const VALIDATION_CACHE_KEY = 'boarderie_validation_cache';

class ValidationCache {
  static #getCache() {
    try {
      const cached = sessionStorage.getItem(VALIDATION_CACHE_KEY);
      return cached ? JSON.parse(cached) : { emails: {}, phones: {} };
    } catch {
      return { emails: {}, phones: {} };
    }
  }

  static #setCache(cache) {
    try {
      sessionStorage.setItem(VALIDATION_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // Session storage might be full or unavailable
    }
  }

  static getEmail(email) {
    const cache = this.#getCache();
    return cache.emails[email.toLowerCase()];
  }

  static setEmail(email, result) {
    const cache = this.#getCache();
    cache.emails[email.toLowerCase()] = result;
    this.#setCache(cache);
  }

  static getPhone(phone) {
    const cache = this.#getCache();
    const normalized = phone.replace(/\D/g, '');
    return cache.phones[normalized];
  }

  static setPhone(phone, result) {
    const cache = this.#getCache();
    const normalized = phone.replace(/\D/g, '');
    cache.phones[normalized] = result;
    this.#setCache(cache);
  }
}

class ContactForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.formElements = this.querySelectorAll('[data-contact-form-element]');
  }

  connectedCallback() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    if (window.accessibilityMode) {
      const labels = this.querySelectorAll('label');
      labels.forEach((label) => {
        label.classList.remove('visually-hidden');
      });
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    let errors = false;

    this.formElements.forEach((element) => {
      clearError(element);
      this.clearFieldError(element);
    });

    for (const element of this.formElements) {
      if (element.hasAttribute('data-required') && !element.value.trim()) {
        const formElement = element.closest('.form-element');
        const formElementLabel = formElement.querySelector('label');
        const formElementLabelText = formElementLabel.textContent.trim().replace('*', '').trim();

        let errorMessage = '';
        if (element.hasAttribute('data-error-message')) {
          errorMessage = element.getAttribute('data-error-message');
        } else {
          errorMessage = `${formElementLabelText} is required`;
        }

        displayError(element, errorMessage);
        element.setAttribute('aria-invalid', 'true');

        errors = true;
        continue;
      }

      if (element.type === 'email' && element.value.trim()) {
        const emailResult = await this.validateEmailWithCache(element);
        if (emailResult.error) {
          this.displayFieldError(element, emailResult.error);
          errors = true;
        }
      }

      if (element.type === 'tel' && element.value.trim()) {
        const phoneResult = await this.validatePhoneWithCache(element);
        if (phoneResult.error) {
          this.displayFieldError(element, phoneResult.error);
          errors = true;
        }
      }
    }

    if (errors) {
      const firstErrorElement = this.form.querySelector('[aria-invalid="true"]');
      if (firstErrorElement) {
        firstErrorElement.focus();
      }
      return;
    }

    this.form.submit();
  }

  async validateEmailWithCache(input) {
    const value = input.value.trim();

    const cached = ValidationCache.getEmail(value);
    if (cached) {
      return cached;
    }

    const result = await validateEmail(input);
    const validationResult = {
      valid: !result.error,
      error: result.error || null,
    };

    if (validationResult.valid) {
      ValidationCache.setEmail(value, validationResult);
    }

    return validationResult;
  }

  async validatePhoneWithCache(input) {
    const value = input.value.trim();

    const cached = ValidationCache.getPhone(value);
    if (cached) {
      return cached;
    }

    const result = await validatePhone(input);
    const validationResult = {
      valid: !result.error,
      error: result.error || null,
    };

    if (validationResult.valid) {
      ValidationCache.setPhone(value, validationResult);
    }

    return validationResult;
  }

  displayFieldError(field, errorMessage) {
    const formElement = field.closest('.form-element');
    const formGroup = field.closest('.form-group');

    if (formElement) {
      formElement.classList.add('form-element--error');
    }

    field.classList.add('is-invalid');
    field.setAttribute('aria-invalid', 'true');

    const errorContainer =
      formGroup?.querySelector('.form-element__error-message') ||
      formElement?.querySelector('.form-element__error-message');

    if (errorContainer) {
      errorContainer.textContent = errorMessage;
      errorContainer.classList.add('is-visible');
    } else {
      displayError(field, errorMessage);
    }
  }

  clearFieldError(field) {
    const formElement = field.closest('.form-element');
    const formGroup = field.closest('.form-group');

    if (formElement) {
      formElement.classList.remove('form-element--error');
    }

    field.classList.remove('is-invalid');

    const errorContainer =
      formGroup?.querySelector('.form-element__error-message') ||
      formElement?.querySelector('.form-element__error-message');

    if (errorContainer) {
      errorContainer.textContent = '';
      errorContainer.classList.remove('is-visible');
    }
  }
}

defineElement('contact-form', ContactForm);

export default ContactForm;
